import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import { AdapterOptions, FlatAnswerMutation } from 'components/Assessments/adapters/types'
import { getAnswerValuesFromItems } from 'components/Assessments/helpers'
import { RecertFormData } from 'components/Assessments/RecertAssessment/types/RecertFormData'
import { RecertFormField } from 'components/Assessments/RecertAssessment/types/RecertFormField'
import { RecertAssessmentQuestionnaireItem } from 'components/Assessments/RecertAssessment/types/questionnaire'
import { BaseSubsectionAdapter } from '../../../adapters/BaseSubsectionAdapter'

export class NarrativeSectionAdapter extends BaseSubsectionAdapter<RecertFormData> {
  extractFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    _options?: AdapterOptions
  ): Partial<RecertFormData> {
    const background = getAnswerValuesFromItems(
      [flatAnswers[RecertAssessmentQuestionnaireItem.NARRATIVE_BACKGROUND]],
      RecertAssessmentQuestionnaireItem.NARRATIVE_BACKGROUND
    )?.at(0)
    const systemSummaries = getAnswerValuesFromItems(
      [flatAnswers[RecertAssessmentQuestionnaireItem.NARRATIVE_SYSTEM_SUMMARIES]],
      RecertAssessmentQuestionnaireItem.NARRATIVE_SYSTEM_SUMMARIES
    )?.at(0)
    const clinicalOverview = getAnswerValuesFromItems(
      [flatAnswers[RecertAssessmentQuestionnaireItem.NARRATIVE_CLINICAL_OVERVIEW]],
      RecertAssessmentQuestionnaireItem.NARRATIVE_CLINICAL_OVERVIEW
    )?.at(0)
    const skilledNeeds = getAnswerValuesFromItems(
      [flatAnswers[RecertAssessmentQuestionnaireItem.NARRATIVE_SKILLED_NEEDS]],
      RecertAssessmentQuestionnaireItem.NARRATIVE_SKILLED_NEEDS
    )?.at(0)
    const adlNeeds = getAnswerValuesFromItems(
      [flatAnswers[RecertAssessmentQuestionnaireItem.NARRATIVE_ADL_NEEDS]],
      RecertAssessmentQuestionnaireItem.NARRATIVE_ADL_NEEDS
    )?.at(0)
    const patientServices = getAnswerValuesFromItems(
      [flatAnswers[RecertAssessmentQuestionnaireItem.NARRATIVE_PATIENT_SERVICES]],
      RecertAssessmentQuestionnaireItem.NARRATIVE_PATIENT_SERVICES
    )?.at(0)
    const careCoordination = getAnswerValuesFromItems(
      [flatAnswers[RecertAssessmentQuestionnaireItem.NARRATIVE_CARE_COORDINATION]],
      RecertAssessmentQuestionnaireItem.NARRATIVE_CARE_COORDINATION
    )?.at(0)
    const patientGoals = getAnswerValuesFromItems(
      [flatAnswers[RecertAssessmentQuestionnaireItem.NARRATIVE_PATIENT_GOALS]],
      RecertAssessmentQuestionnaireItem.NARRATIVE_PATIENT_GOALS
    )?.at(0)
    const clinicalEducation = getAnswerValuesFromItems(
      [flatAnswers[RecertAssessmentQuestionnaireItem.NARRATIVE_CLINICAL_EDUCATION]],
      RecertAssessmentQuestionnaireItem.NARRATIVE_CLINICAL_EDUCATION
    )?.at(0)
    const finalNotes = getAnswerValuesFromItems(
      [flatAnswers[RecertAssessmentQuestionnaireItem.NARRATIVE_FINAL_NOTES]],
      RecertAssessmentQuestionnaireItem.NARRATIVE_FINAL_NOTES
    )?.at(0)

    return {
      [RecertFormField.NARRATIVE_BACKGROUND]: background,
      [RecertFormField.NARRATIVE_SYSTEM_SUMMARIES]: systemSummaries,
      [RecertFormField.NARRATIVE_CLINICAL_OVERVIEW]: clinicalOverview,
      [RecertFormField.NARRATIVE_SKILLED_NEEDS]: skilledNeeds,
      [RecertFormField.NARRATIVE_ADL_NEEDS]: adlNeeds,
      [RecertFormField.NARRATIVE_PATIENT_SERVICES]: patientServices,
      [RecertFormField.NARRATIVE_CARE_COORDINATION]: careCoordination,
      [RecertFormField.NARRATIVE_PATIENT_GOALS]: patientGoals,
      [RecertFormField.NARRATIVE_CLINICAL_EDUCATION]: clinicalEducation,
      [RecertFormField.NARRATIVE_FINAL_NOTES]: finalNotes
    }
  }
  buildFlatAnswers(): FlatAnswerMutation[] {
    const mutations: FlatAnswerMutation[] = []

    if (this.formDataFieldExists(RecertFormField.NARRATIVE_BACKGROUND)) {
      mutations.push({
        linkId: RecertAssessmentQuestionnaireItem.NARRATIVE_BACKGROUND,
        value: this.getFormDataField(RecertFormField.NARRATIVE_BACKGROUND)
      })
    }

    if (this.formDataFieldExists(RecertFormField.NARRATIVE_SYSTEM_SUMMARIES)) {
      mutations.push({
        linkId: RecertAssessmentQuestionnaireItem.NARRATIVE_SYSTEM_SUMMARIES,
        value: this.getFormDataField(RecertFormField.NARRATIVE_SYSTEM_SUMMARIES)
      })
    }

    if (this.formDataFieldExists(RecertFormField.NARRATIVE_CLINICAL_OVERVIEW)) {
      mutations.push({
        linkId: RecertAssessmentQuestionnaireItem.NARRATIVE_CLINICAL_OVERVIEW,
        value: this.getFormDataField(RecertFormField.NARRATIVE_CLINICAL_OVERVIEW)
      })
    }

    if (this.formDataFieldExists(RecertFormField.NARRATIVE_SKILLED_NEEDS)) {
      mutations.push({
        linkId: RecertAssessmentQuestionnaireItem.NARRATIVE_SKILLED_NEEDS,
        value: this.getFormDataField(RecertFormField.NARRATIVE_SKILLED_NEEDS)
      })
    }

    if (this.formDataFieldExists(RecertFormField.NARRATIVE_ADL_NEEDS)) {
      mutations.push({
        linkId: RecertAssessmentQuestionnaireItem.NARRATIVE_ADL_NEEDS,
        value: this.getFormDataField(RecertFormField.NARRATIVE_ADL_NEEDS)
      })
    }

    if (this.formDataFieldExists(RecertFormField.NARRATIVE_PATIENT_SERVICES)) {
      mutations.push({
        linkId: RecertAssessmentQuestionnaireItem.NARRATIVE_PATIENT_SERVICES,
        value: this.getFormDataField(RecertFormField.NARRATIVE_PATIENT_SERVICES)
      })
    }

    if (this.formDataFieldExists(RecertFormField.NARRATIVE_CARE_COORDINATION)) {
      mutations.push({
        linkId: RecertAssessmentQuestionnaireItem.NARRATIVE_CARE_COORDINATION,
        value: this.getFormDataField(RecertFormField.NARRATIVE_CARE_COORDINATION)
      })
    }

    if (this.formDataFieldExists(RecertFormField.NARRATIVE_PATIENT_GOALS)) {
      mutations.push({
        linkId: RecertAssessmentQuestionnaireItem.NARRATIVE_PATIENT_GOALS,
        value: this.getFormDataField(RecertFormField.NARRATIVE_PATIENT_GOALS)
      })
    }

    if (this.formDataFieldExists(RecertFormField.NARRATIVE_CLINICAL_EDUCATION)) {
      mutations.push({
        linkId: RecertAssessmentQuestionnaireItem.NARRATIVE_CLINICAL_EDUCATION,
        value: this.getFormDataField(RecertFormField.NARRATIVE_CLINICAL_EDUCATION)
      })
    }

    if (this.formDataFieldExists(RecertFormField.NARRATIVE_FINAL_NOTES)) {
      mutations.push({
        linkId: RecertAssessmentQuestionnaireItem.NARRATIVE_FINAL_NOTES,
        value: this.getFormDataField(RecertFormField.NARRATIVE_FINAL_NOTES)
      })
    }

    return mutations
  }
}
